// @ts-nocheck - untyped legacy service calls
import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import {
  MaterialReactTable,
  useMaterialReactTable,
  type MRT_ColumnDef,
  type MRT_ColumnFiltersState,
  type MRT_Updater,
} from 'material-react-table';
import UserManagementService from '../../../Services/UserManagementService';
import Notification from '../../Layout/Notification';
import UserDetails from './UserDetails';
import { alpha, useTheme } from '@mui/material/styles';
import './UserManagement.scss';

const StatusPill = ({ active }: { active: boolean }) => {
  const theme = useTheme();
  const fg = active ? theme.tokens.successFg : theme.tokens.dangerFg;
  return (
    <span
      style={{
        fontSize: 11,
        fontWeight: 600,
        padding: '3px 8px',
        borderRadius: 6,
        color: fg,
        backgroundColor: active ? theme.tokens.successBg : theme.tokens.dangerBg,
        border: `1px solid ${alpha(fg, 0.2)}`,
      }}
    >
      {active ? 'Active' : 'Inactive'}
    </span>
  );
};

function filtersToParams(columnFilters: MRT_ColumnFiltersState) {
  const params: Record<string, string> = {};
  for (const { id, value } of columnFilters) {
    if (!value && value !== false) continue;
    const v = String(value).trim();
    if (!v) continue;
    switch (id) {
      case 'firstName':
        params.firstName = v;
        break;
      case 'lastName':
        params.lastName = v;
        break;
      case 'email':
        params.email = v;
        break;
      case 'name':
        params.tenantName = v;
        break;
      case 'tenantKey':
        params.tenantKey = v;
        break;
      case 'activated':
        if (v === 'Active') params.activated = 'true';
        else if (v === 'Inactive') params.activated = 'false';
        break;
    }
  }
  return params;
}

interface UsersProps {
  onMount?: (actions: {
    add: () => void;
    export: () => void;
    toggleFilters: () => void;
    toggleFullScreen: () => void;
  }) => void;
}

const Users = ({ onMount }: UsersProps) => {
  const theme = useTheme();
  const ACCENT = theme.tokens.accent;
  const [dialogOpen, setDialogOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [users, setUsers] = useState([]);
  const [totalUsers, setTotalUsers] = useState(0);
  const [loading, setLoading] = useState(true);
  const [sortBy, setSortBy] = useState('firstName');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [columnFilters, setColumnFilters] = useState<MRT_ColumnFiltersState>([]);

  const debounceRef = useRef<ReturnType<typeof setTimeout>>();

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const filterParams = filtersToParams(columnFilters);
      const response = await UserManagementService.getUsers({
        page,
        size: rowsPerPage,
        sort: sortBy,
        order: sortOrder,
        ...filterParams,
      });
      setUsers(response.data.content);
      setTotalUsers(response.data.totalElements);
    } catch (err) {
      Notification.fromError(err, { fallbackTitle: "Couldn't load users" });
    } finally {
      setLoading(false);
    }
  }, [page, rowsPerPage, sortBy, sortOrder, columnFilters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleExport = useCallback(async () => {
    setLoading(true);
    const filterParams = filtersToParams(columnFilters);
    await UserManagementService.exportUsers({ sort: sortBy, order: sortOrder, ...filterParams });
    setLoading(false);
  }, [columnFilters, sortBy, sortOrder]);

  const handleViewDetails = (user: Record<string, unknown>) => {
    setSelectedUser(user);
    setDialogOpen(true);
  };
  const handleAddUser = useCallback(() => {
    setSelectedUser(null);
    setDialogOpen(true);
  }, []);
  const handleCloseDialog = () => {
    setDialogOpen(false);
    setSelectedUser(null);
  };

  const handleColumnFiltersChange = useCallback(
    (updaterOrValue: MRT_Updater<MRT_ColumnFiltersState>) => {
      clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(() => {
        setColumnFilters(updaterOrValue);
        setPage(0);
      }, 350);
    },
    []
  );

  type UserRow = Record<string, unknown> & {
    tenant?: { name?: string; tenantKey?: string };
    activated?: boolean;
  };

  const columns = useMemo<MRT_ColumnDef<UserRow>[]>(
    () => [
      { accessorKey: 'firstName', header: 'First Name', size: 140, filterVariant: 'text' },
      { accessorKey: 'lastName', header: 'Last Name', size: 140, filterVariant: 'text' },
      { accessorKey: 'email', header: 'Email Address', size: 220, filterVariant: 'text' },
      {
        accessorFn: (r: UserRow) => r.tenant?.name,
        id: 'name',
        header: 'Tenant Name',
        size: 160,
        filterVariant: 'text',
      },
      {
        accessorFn: (r: UserRow) => r.tenant?.tenantKey,
        id: 'tenantKey',
        header: 'Tenant Key',
        size: 130,
        filterVariant: 'text',
      },
      {
        accessorFn: (r: UserRow) => (r.activated ? 'Active' : 'Inactive'),
        id: 'activated',
        header: 'Active',
        size: 100,
        enableSorting: false,
        filterVariant: 'select',
        filterSelectOptions: ['Active', 'Inactive'],
        Cell: ({ row }) => <StatusPill active={row.original.activated} />,
      },
    ],
    []
  );

  const sorting = useMemo(() => [{ id: sortBy, desc: sortOrder === 'desc' }], [sortBy, sortOrder]);

  const table = useMaterialReactTable({
    data: users,
    columns,
    manualSorting: true,
    manualPagination: true,
    manualFiltering: true,
    rowCount: totalUsers,
    enableColumnActions: false,
    enableColumnFilters: true,
    enableGlobalFilter: false,
    enableHiding: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableSortingRemoval: false,
    enableMultiSort: false,
    enableTopToolbar: true,
    enableToolbarInternalActions: false,
    renderTopToolbar: ({ table }) =>
      table.getState().isFullScreen ? (
        <div
          style={{
            display: 'flex',
            justifyContent: 'flex-end',
            padding: '4px 8px',
            backgroundColor: 'var(--t-surface-subtle)',
            borderBottom: '1px solid var(--t-border, rgba(0,0,0,0.12))',
          }}
        >
          <button
            onClick={() => table.setIsFullScreen(false)}
            style={{
              background: 'none',
              border: 'none',
              cursor: 'pointer',
              color: ACCENT,
              fontSize: 13,
              fontWeight: 600,
              display: 'flex',
              alignItems: 'center',
              gap: 4,
            }}
            title="Exit Fullscreen"
          >
            ✕ Exit Fullscreen
          </button>
        </div>
      ) : (
        <div style={{ display: 'none' }} />
      ),
    enableFilterMatchHighlighting: false,
    enableStickyHeader: true,
    columnFilterDisplayMode: 'subheader',
    initialState: { density: 'compact', showColumnFilters: false },
    state: {
      sorting,
      columnFilters,
      pagination: { pageIndex: page, pageSize: rowsPerPage },
      isLoading: loading,
    },
    onColumnFiltersChange: handleColumnFiltersChange,
    onSortingChange: updater => {
      const next = typeof updater === 'function' ? updater(sorting) : updater;
      if (next[0]) {
        setSortBy(next[0].id);
        setSortOrder(next[0].desc ? 'desc' : 'asc');
      }
    },
    onPaginationChange: updater => {
      const prev = { pageIndex: page, pageSize: rowsPerPage };
      const next = typeof updater === 'function' ? updater(prev) : updater;
      setPage(next.pageIndex);
      setRowsPerPage(next.pageSize);
    },
    muiTableBodyRowProps: ({ row }) => ({
      onClick: () => handleViewDetails(row.original),
      sx: { cursor: 'pointer', '&:hover': { backgroundColor: 'rgba(94,51,191,0.04)' } },
    }),
    paginationDisplayMode: 'pages',
    muiTablePaperProps: {
      elevation: 0,
      sx: {
        borderRadius: 2,
        border: '1px solid var(--t-border, rgba(0,0,0,0.12))',
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
        minHeight: 0,
      },
    },
    muiTableContainerProps: {
      sx: { flex: 1, overflow: 'auto' },
    },
    muiTableHeadCellProps: {
      sx: {
        fontWeight: 700,
        fontSize: 13,
        backgroundColor: 'var(--t-surface-subtle)',
        borderBottom: `2px solid ${ACCENT}`,
      },
    },
    muiFilterTextFieldProps: {
      variant: 'outlined',
      size: 'small',
      sx: {
        '& .MuiInputBase-root': { fontSize: 12, borderRadius: '6px' },
        '& .MuiInputAdornment-positionEnd:has(.Mui-disabled)': {
          display: 'none',
        },
      },
    },
    muiFilterSelectProps: {
      variant: 'outlined',
      size: 'small',
      sx: { fontSize: 12, borderRadius: '6px' },
    },
    muiTableBodyCellProps: {
      sx: { fontSize: 13 },
    },
    muiBottomToolbarProps: {
      sx: {
        backgroundColor: 'var(--t-surface-subtle)',
        minHeight: 40,
        '& .MuiBox-root': { justifyContent: 'center' },
      },
    },
  });

  // Register actions with parent (after table is created)
  useEffect(() => {
    onMount?.({
      add: handleAddUser,
      export: handleExport,
      toggleFilters: () => table.setShowColumnFilters((prev: boolean) => !prev),
      toggleFullScreen: () => table.setIsFullScreen((prev: boolean) => !prev),
    });
  }, [onMount, handleAddUser, handleExport, table]);

  return (
    <div
      id="users-main-container"
      style={{ display: 'flex', flexDirection: 'column', height: '100%' }}
    >
      <MaterialReactTable table={table} />
      <UserDetails
        open={dialogOpen}
        user={selectedUser}
        onClose={handleCloseDialog}
        onUpdateData={fetchData}
      />
    </div>
  );
};

export default Users;
